import type { InputRouter, LayerHandle } from "../../ui/input/router.ts";
import { pageKeys, type KeyTable } from "../controls/page.ts";
import { actionPress } from "../controls/press.ts";

/**
 * What the keys need of the store: whether the window and its places panel are open, to open or shut the window, to
 * centre the view, and what Escape does to the panel.
 */
export interface WorldMapKeysTarget {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => { readonly open: boolean; readonly panelOpen: boolean; readonly presentation: string; readonly level: string };
  readonly toggle: () => void;
  readonly close: () => void;
  readonly centre: () => void;
  readonly zoomIn: () => void;
  readonly zoomOut: () => void;
  readonly escapePanel: () => void;
}

/**
 * The world map's keys (P2.12-T6, ADR-100 decision 1): the key the page's key table binds World map to (M by default;
 * P1.9-T50, `controls/`), read on each press, opens and shuts the window while the game owns the keyboard, so never
 * while a text field has focus or a modal holds the keys. While the window is open it is a layer of the input router
 * that names only C, which returns the view to the character at Region (and does nothing at Nearby, which has no pan),
 * and `-`, `=` and `+`, which step the ladder (P2.12-T21, decision 41: layer keys of the window, in no key table, so
 * nothing rebinds and nothing is taken while the Map is shut), so Escape closes it as it closes any topmost layer and
 * every other key reaches the game as though nothing were open (ADR-061's amendment "a surface that is not modal never
 * takes the game's keys"): the world does not pause and the movement keys still move the character. An open places
 * panel is a second layer above the window's, so Escape answers it first: it clears the panel's filter while there is
 * one, then shuts the panel, and only then the window (ADR-100's note of 2026-10-14). The float is not a layer
 * (P2.12-T23, decision 49): while the Map is shown as the float none of these layers is open, so Escape, C and the zoom
 * keys all reach the game, and M still shuts it and opens it.
 *
 * @param store The world map's store, whose open state and presentation the layer follows.
 * @param input The page's input router.
 * @param keys The key table, whose World map key opens and shuts the window; the page's unless given.
 * @returns A function that unbinds the key and closes the window's layer.
 * @example
 * bindWorldMapKeys(worldMap, input);
 */
export function bindWorldMapKeys(store: WorldMapKeysTarget, input: Pick<InputRouter, "open" | "onKey">, keys: Pick<KeyTable, "actionOf"> = pageKeys): () => void {
  let layer: LayerHandle | null = null;
  let panelLayer: LayerHandle | null = null;
  const zoom = {
    /**
     * Zooms the Map out one step.
     */
    "-": () => store.zoomOut(),
    /**
     * Zooms the Map in one step.
     */
    "=": () => store.zoomIn(),
    /**
     * Zooms the Map in one step.
     */
    "+": () => store.zoomIn(),
  };

  /**
   * Makes the layers follow the store's open state: the window's, then the panel's above it while the window is open.
   */
  const follow = (): void => {
    const snapshot = store.getSnapshot();
    const open = snapshot.open && snapshot.presentation === "window";
    const panelOpen = snapshot.panelOpen && snapshot.level === "region";
    if ((!open || !panelOpen) && panelLayer !== null) {
      const shut = panelLayer;
      panelLayer = null;
      shut.close();
    }
    if (open && layer === null) {
      layer = input.open({
        onClose: () => {
          if (layer === null) {
            return;
          }
          layer = null;
          store.close();
        },
        keys: { c: () => store.centre(), ...zoom },
      });
    } else if (!open && layer !== null) {
      const opened = layer;
      layer = null;
      opened.close();
    }
    if (open && panelOpen && panelLayer === null) {
      panelLayer = input.open({
        onClose: () => {
          if (panelLayer === null) {
            return;
          }
          panelLayer = null;
          store.escapePanel();
        },
        keys: zoom,
      });
    }
  };
  const unsubscribe = store.subscribe(follow);
  const unlisten = input.onKey((event) => {
    if (event.repeat || event.defaultPrevented || !actionPress(keys, "worldMap", event) || event.ctrlKey === true || event.altKey === true || event.metaKey === true) {
      return;
    }
    event.preventDefault();
    store.toggle();
  });
  follow();
  return () => {
    unsubscribe();
    unlisten();
    const shut = panelLayer;
    panelLayer = null;
    shut?.close();
    const opened = layer;
    layer = null;
    opened?.close();
  };
}
